import { useEffect, useRef, useState } from 'react'
import { Modal } from '../Modal'
import { useFormDraft } from '../../hooks/useFormDraft'
import {
  formatCpfCnpj,
  fornecedorToInput,
  normalizeFornecedorInput,
  salvarFornecedor,
  validateFornecedorInput,
  type FornecedorInput,
} from '../../lib/fornecedorService'
import type { Fornecedor, TipoPessoa } from '../../types/database'
import { TIPO_PESSOA_LABELS, UFS_BR } from '../../types/database'

const emptyForm: FornecedorInput = {
  razao_social: '',
  nome_fantasia: null,
  tipo_pessoa: 'PJ',
  cpf_cnpj: null,
  cep: null,
  logradouro: null,
  numero: null,
  complemento: null,
  bairro: null,
  cidade: null,
  uf: null,
  responsavel_nome: null,
  telefone: null,
  celular: null,
  email: null,
  ativo: true,
}

const DRAFT_KEY_CREATE = 'isen-educae:draft:fornecedor:create'

interface FornecedorFormModalProps {
  open: boolean
  mode: 'create' | 'edit'
  fornecedor?: Fornecedor
  onClose: () => void
  onSaved: () => void
  onError: (message: string) => void
}

export function FornecedorFormModal({
  open,
  mode,
  fornecedor,
  onClose,
  onSaved,
  onError,
}: FornecedorFormModalProps) {
  const razaoRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const isCreate = mode === 'create'
  const [form, setForm, clearDraft] = useFormDraft(
    DRAFT_KEY_CREATE,
    emptyForm,
    open && isCreate,
  )

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && fornecedor) {
      setForm(fornecedorToInput(fornecedor))
    }
    setTimeout(() => razaoRef.current?.focus(), 50)
  }, [open, mode, fornecedor, setForm])

  function handleClose() {
    onClose()
  }

  function setField<K extends keyof FornecedorInput>(key: K, value: FornecedorInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    const normalized = normalizeFornecedorInput(form)
    const validation = validateFornecedorInput(normalized)
    if (validation) {
      onError(validation)
      return
    }

    setBusy(true)
    try {
      const { error } = await salvarFornecedor(normalized, mode === 'edit' ? fornecedor?.id : undefined)
      if (error) throw new Error(error)
      if (isCreate) {
        clearDraft()
        try {
          sessionStorage.removeItem('isen-educae:ui:fornecedor-modal')
        } catch {
          /* ignore */
        }
      }
      onSaved()
      onClose()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erro ao salvar fornecedor.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={mode === 'edit' ? 'Editar fornecedor' : 'Novo fornecedor'}
      onClose={handleClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={busy}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy}>
            {busy ? 'Salvando…' : 'Salvar fornecedor'}
          </button>
        </>
      }
    >
      <fieldset className="form-section" disabled={busy}>
        <legend>Identificação</legend>
        <div className="form-grid">
          <div className="field field-span-2">
            <label htmlFor="forn-razao">
              Razão social <span aria-hidden>*</span>
            </label>
            <input
              id="forn-razao"
              ref={razaoRef}
              value={form.razao_social}
              onChange={(e) => setField('razao_social', e.target.value)}
            />
          </div>
          <div className="field field-span-2">
            <label htmlFor="forn-fantasia">Nome fantasia</label>
            <input
              id="forn-fantasia"
              value={form.nome_fantasia || ''}
              onChange={(e) => setField('nome_fantasia', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-tipo">Tipo de pessoa</label>
            <select
              id="forn-tipo"
              value={form.tipo_pessoa}
              onChange={(e) => setField('tipo_pessoa', e.target.value as TipoPessoa)}
            >
              {(Object.keys(TIPO_PESSOA_LABELS) as TipoPessoa[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_PESSOA_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="forn-doc">{form.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'}</label>
            <input
              id="forn-doc"
              value={form.cpf_cnpj || ''}
              onChange={(e) =>
                setField('cpf_cnpj', formatCpfCnpj(e.target.value, form.tipo_pessoa) || null)
              }
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="form-section" disabled={busy}>
        <legend>Endereço</legend>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="forn-cep">CEP</label>
            <input
              id="forn-cep"
              value={form.cep || ''}
              onChange={(e) => setField('cep', e.target.value || null)}
            />
          </div>
          <div className="field field-span-2">
            <label htmlFor="forn-logradouro">Logradouro</label>
            <input
              id="forn-logradouro"
              value={form.logradouro || ''}
              onChange={(e) => setField('logradouro', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-numero">Número</label>
            <input
              id="forn-numero"
              value={form.numero || ''}
              onChange={(e) => setField('numero', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-complemento">Complemento</label>
            <input
              id="forn-complemento"
              value={form.complemento || ''}
              onChange={(e) => setField('complemento', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-bairro">Bairro</label>
            <input
              id="forn-bairro"
              value={form.bairro || ''}
              onChange={(e) => setField('bairro', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-cidade">Cidade</label>
            <input
              id="forn-cidade"
              value={form.cidade || ''}
              onChange={(e) => setField('cidade', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-uf">UF</label>
            <select
              id="forn-uf"
              value={form.uf || ''}
              onChange={(e) => setField('uf', e.target.value || null)}
            >
              <option value="">—</option>
              {UFS_BR.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>
      </fieldset>

      <fieldset className="form-section" disabled={busy}>
        <legend>Contatos</legend>
        <div className="form-grid">
          <div className="field field-span-2">
            <label htmlFor="forn-responsavel">Nome do vendedor ou responsável</label>
            <input
              id="forn-responsavel"
              value={form.responsavel_nome || ''}
              onChange={(e) => setField('responsavel_nome', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-telefone">Telefone</label>
            <input
              id="forn-telefone"
              value={form.telefone || ''}
              onChange={(e) => setField('telefone', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-celular">Celular</label>
            <input
              id="forn-celular"
              value={form.celular || ''}
              onChange={(e) => setField('celular', e.target.value || null)}
            />
          </div>
          <div className="field field-span-2">
            <label htmlFor="forn-email">E-mail principal</label>
            <input
              id="forn-email"
              type="email"
              value={form.email || ''}
              onChange={(e) => setField('email', e.target.value || null)}
            />
          </div>
          <div className="field">
            <label htmlFor="forn-ativo">Ativo</label>
            <select
              id="forn-ativo"
              value={form.ativo ? 'sim' : 'nao'}
              onChange={(e) => setField('ativo', e.target.value === 'sim')}
            >
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </div>
        </div>
      </fieldset>
    </Modal>
  )
}
